import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getLocalStorage, getWithToken, patchWithToken, postWithToken } from '../API/Api';

import Loader from '../components/Loader';
import SAVE_ICON from '../assets/icons/ComniqaiIcon-V3.0/save-icon.svg';
import SubContext from '../contexts/SubContext';
import { getPlanLevel, PLAN_LEVELS } from '../API/Constant';
import { ChevronDown, ChevronUp } from 'lucide-react';

const OpenAIConfiguration = () => {
  const navigate = useNavigate();
  const subsFlag = useContext(SubContext);

  const subscriptionInfo = subsFlag?.subscriptionFlag?.user?.subscription ?? null;
  const subscriptionName = subscriptionInfo?.name ?? 'Free';
  const planLevel = useMemo(() => getPlanLevel(subscriptionName), [subscriptionName]);

  const [loader, setLoader] = useState(false);
  const [apiTokenData, setApiTokenData] = useState(null);
  const [values, setValues] = useState({
    model_name: '',
    api_key: '',
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const token = getLocalStorage('apiToken');
    if (token) {
      const parsedToken = JSON.parse(token);
      setApiTokenData(parsedToken);
      fetchConfigurationDetails(parsedToken);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchConfigurationDetails = async (token) => {
    try {
      setLoader(true);
      const response = await getWithToken('openai_configurations', token);
      if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
        setValues(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch configuration.');
    } finally {
      setLoader(false);
    }
  };

  const handleSaveClick = async () => {
    if (!values.model_name) {
      toast.error('Please select a model name.');
      return;
    }

    const payload = {
      model_name: values.model_name,
      api_key: values.api_key,
    };

    try {
      let response;

      if (values?.id) {
        response = await patchWithToken(`openai_configurations/${values.id}`, apiTokenData, payload);
      } else {
        response = await postWithToken('openai_configurations', apiTokenData, payload);
      }

      const { status, message } = response;

      if (status === 200 || status === 201) {
        toast.success(message || 'Configuration saved successfully.');
        fetchConfigurationDetails(apiTokenData);
      } else {
        toast.error(message || 'Something went wrong.');
      }
    } catch (error) {
      toast.error('Error while saving configuration.');
    }
  };

  const modelOptions = [
    { value: 'gpt-3.5-turbo-16k', label: 'gpt-3.5-turbo-16k' },
    { value: 'gpt-4o-mini', label: 'gpt-4o-mini' },
    ...(planLevel >= PLAN_LEVELS.Growth
      ? [
          { value: 'gpt-4o', label: 'gpt-4o' },
          { value: 'gpt-5', label: 'gpt-5' },
          { value: 'gpt-5-mini', label: 'gpt-5-mini' },
        ]
      : []),
  ];

  return (
    <>
      <div className='setting-card-section'>
        <div className='setting-card-text'>
          <p className='outfit-regular'>Choose your preferred OpenAI model. Advanced models like GPT-4o and GPT-5 are available on higher-tier plans.</p>
        </div>

        <form className='openai-config-form mt-4'>
          {' '}
          <div className='row chatbox-field mt-4'>
            <div className='col-12 col-md-6'>
              <div className='select-custom-dropdown-container' ref={dropdownRef}>
                <label className='select-custom-dropdown-label outfit-medium'>Model Name</label>
                <div className='select-custom-dropdown-selected' onClick={() => setDropdownOpen((prev) => !prev)}>
                  {values.model_name || 'Choose the model'}
                  <span className='select-custom-dropdown-arrow'>{dropdownOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
                </div>

                {dropdownOpen && (
                  <ul className='select-custom-dropdown-options'>
                    {modelOptions.map((option) => (
                      <li
                        key={option.value}
                        className='select-custom-dropdown-option'
                        onClick={() => {
                          setValues((prev) => ({ ...prev, model_name: option.value }));
                          setDropdownOpen(false);
                        }}
                      >
                        {option.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className='d-flex align-items-center justify-content-start mt-3'>
                <button type='button' className='chatbot-save-btn outfit-medium d-flex align-items-center gap-2' onClick={handleSaveClick}>
                  <img src={SAVE_ICON} alt='save-icon' />
                  Save
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {loader && (
        <div className='loader-center'>
          <Loader />
        </div>
      )}
    </>
  );
};

export default OpenAIConfiguration;
